/**
 * Document-head SEO helpers. Origins and brand come from env / live location —
 * never hardcode a marketing domain.
 */

export type DocumentSeoInput = {
  readonly title: string;
  readonly description: string;
  readonly canonicalPath: string;
  readonly siteName: string;
  readonly robots?: string;
  readonly locale?: string;
  readonly alternateLocales?: readonly string[];
  readonly ogType?: string;
  readonly imagePath?: string;
  readonly jsonLd?: Readonly<Record<string, unknown>> | null;
};

const JSON_LD_SCRIPT_ID = 'platform-seo-jsonld';

function stripTrailingSlash(value: string): string {
  return value.replace(/\/$/, '');
}

/** Prefer VITE_PUBLIC_SITE_ORIGIN; otherwise the live browser origin. */
export function resolvePublicSiteOrigin(preferredOrigin?: string): string {
  const preferred = preferredOrigin?.trim();
  if (preferred !== undefined && preferred.length > 0) {
    return stripTrailingSlash(preferred);
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return stripTrailingSlash(window.location.origin);
  }
  return '';
}

function absoluteUrl(origin: string, path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  if (origin.length === 0) {
    return path.startsWith('/') ? path : `/${path}`;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${origin}${normalized}`;
}

function upsertMetaByName(name: string, content: string): void {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!(el instanceof HTMLMetaElement)) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertMetaByProperty(property: string, content: string): void {
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!(el instanceof HTMLMetaElement)) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLinkRel(rel: string, href: string, hreflang?: string): void {
  const selector =
    hreflang === undefined
      ? `link[rel="${rel}"]:not([hreflang])`
      : `link[rel="${rel}"][hreflang="${hreflang}"]`;
  let el = document.head.querySelector(selector);
  if (!(el instanceof HTMLLinkElement)) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang !== undefined) {
      el.setAttribute('hreflang', hreflang);
    }
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(data: Readonly<Record<string, unknown>> | null): void {
  const existing = document.getElementById(JSON_LD_SCRIPT_ID);
  if (data === null) {
    existing?.remove();
    return;
  }
  let script: HTMLScriptElement;
  if (existing instanceof HTMLScriptElement) {
    script = existing;
  } else {
    existing?.remove();
    script = document.createElement('script');
    script.id = JSON_LD_SCRIPT_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export function applyDocumentSeo(
  input: DocumentSeoInput,
  preferredOrigin?: string,
): string {
  const origin = resolvePublicSiteOrigin(preferredOrigin);
  const canonical = absoluteUrl(origin, input.canonicalPath);
  const robots = input.robots ?? 'index,follow';
  const ogType = input.ogType ?? 'website';
  const locale = input.locale ?? 'cs_CZ';

  document.title = input.title;
  upsertMetaByName('description', input.description);
  upsertMetaByName('robots', robots);
  upsertMetaByName('twitter:card', 'summary_large_image');
  upsertMetaByName('twitter:title', input.title);
  upsertMetaByName('twitter:description', input.description);

  upsertMetaByProperty('og:type', ogType);
  upsertMetaByProperty('og:site_name', input.siteName);
  upsertMetaByProperty('og:title', input.title);
  upsertMetaByProperty('og:description', input.description);
  upsertMetaByProperty('og:url', canonical);
  upsertMetaByProperty('og:locale', locale);

  if (canonical.startsWith('http://') || canonical.startsWith('https://')) {
    upsertLinkRel('canonical', canonical);
    const alternates = input.alternateLocales ?? [];
    for (const alt of alternates) {
      upsertLinkRel('alternate', canonical, alt);
    }
    upsertLinkRel('alternate', canonical, 'x-default');
  }

  if (input.imagePath !== undefined && input.imagePath.length > 0 && origin.length > 0) {
    const imageUrl = absoluteUrl(origin, input.imagePath);
    upsertMetaByProperty('og:image', imageUrl);
    upsertMetaByName('twitter:image', imageUrl);
  }

  upsertJsonLd(input.jsonLd ?? null);
  return canonical;
}

export function buildOrganizationJsonLd(input: {
  readonly name: string;
  readonly url: string;
  readonly description: string;
  readonly sameAs?: readonly string[];
}): Readonly<Record<string, unknown>> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: input.name,
    url: input.url,
    description: input.description,
    ...(input.sameAs !== undefined && input.sameAs.length > 0
      ? { sameAs: [...input.sameAs] }
      : {}),
  };
}

export function buildWebSiteJsonLd(input: {
  readonly name: string;
  readonly url: string;
  readonly description: string;
  readonly searchTargetPath?: string;
}): Readonly<Record<string, unknown>> {
  const base: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: input.name,
    url: input.url,
    description: input.description,
    inLanguage: ['cs', 'sk', 'en'],
  };
  if (input.searchTargetPath !== undefined && input.searchTargetPath.length > 0) {
    base['potentialAction'] = {
      '@type': 'SearchAction',
      target: `${input.url}${input.searchTargetPath}`,
      'query-input': 'required name=search_term_string',
    };
  }
  return base;
}